import { useEditor, EditorContent } from "@tiptap/react";
import { Placeholder } from "@tiptap/extensions/placeholder";
import StarterKit from "@tiptap/starter-kit";
import ResizeImage from "tiptap-extension-resize-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import Youtube from "@tiptap/extension-youtube";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { useCallback, useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import toast from "@/utils/toast";
import "@/css/rich-text-editor.css";
import { TaskMedia } from "@/api/types";
import { apiClient } from "@/api";
import { API_BASE_URL } from "@/env";
import React from "react";
import { RichTextToolbar } from "./RichTextToolbar";

interface RichTextEditorProps {
  initialValue?: string; // HTML string or serialized JSON string of the editor state
  onChange?: (value: string) => void; // Callback with HTML string
  readOnly?: boolean; // Whether the editor is read-only
  rows?: number; // Number of rows (for height)
  placeholder?: string; // Placeholder text
  className?: string; // Additional CSS classes
  embedImagesInBody?: boolean; // if true, embeds images as base64 rather than uploading
  ownerId?: string; // ID of the owner, required if embedImagesInBody is false
  ownerType?: "task" | "prompt"; // Type of the owner, either "task" or "prompt"
  enabledToolbarBlocks?: Array<
    "formatting" | "lists" | "tables" | "colors" | "media" | "history"
  >; // Which toolbar blocks to show
  disabled?: boolean; // Whether the editor is disabled (no interaction)
}

export interface RichTextEditorRef {
  focus: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  initialValue = "",
  onChange,
  readOnly = false,
  rows = 3,
  placeholder = "Start typing...",
  className = "",
  embedImagesInBody = false,
  ownerId,
  ownerType,
  enabledToolbarBlocks = [
    "formatting",
    "lists",
    "tables",
    "colors",
    "media",
    "history",
  ],
  disabled = false,
}, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
      ResizeImage.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg",
        },
        allowBase64: true,
        inline: false,
      }),
      // Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Youtube.configure({
        controls: false,
        nocookie: true,
        HTMLAttributes: {
          class: "rounded-lg max-w-full",
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialValue,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (editor && !editor.isFocused) {
        editor.commands.focus("end");
      }
    },
  }), [editor]);

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be smaller than 5MB");
        return;
      }

      setIsUploading(true);

      if (!embedImagesInBody) {
        // Upload to server
        // NOTE: if we don't have a taskPromptId (e.g., when creating a new task,
        // since the task is entirely client-side), the media will start out as
        // orphaned. if the task is created, the backend will associate the media
        // with the new prompt. if the new task is abandoned, a management command
        // will clean up orphaned media after a few hours.

        console.log("Attempting to upload image to server");

        const formData = new FormData();
        if (ownerId && ownerType) {
          formData.append(`owner_${ownerType}`, ownerId);
        }
        formData.append("media_file", file);

        console.log("Uploading image to server...", formData);
        // print the contents of formData
        for (let pair of formData.entries()) {
          console.log(pair[0] + ", " + pair[1]);
        }

        apiClient
          .post<TaskMedia>(`/task-prompt-media/`, formData, {
            requireAuth: true,
            retryLogin: true,
          })
          .then((response: TaskMedia) => {
            if (editor) {
              editor
                .chain()
                .focus()
                .setImage({ src: `${API_BASE_URL}${response.mediaUrl}` })
                .run();
              toast.success("Image uploaded successfully");
            }
          })
          .catch((error) => {
            console.error("Image upload failed:", error);
            toast.error("Failed to upload image");
          })
          .finally(() => {
            setIsUploading(false);
          });
      } else {
        // Convert to base64 and embed directly into the body
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result && editor) {
            editor
              .chain()
              .focus()
              .setImage({ src: reader.result as string })
              .run();
            setIsUploading(false);
            toast.success("Image uploaded successfully");
          }
        };
        reader.onerror = () => {
          setIsUploading(false);
          toast.error("Failed to upload image");
        };
        reader.readAsDataURL(file);
      }

      // Reset file input
      event.target.value = "";
    },
    [editor]
  );

  useEffect(() => {
    if (disabled) {
      editor?.setOptions({ editable: false });
    } else if (!readOnly) {
      editor?.setOptions({ editable: true });
    }
  }, [disabled, readOnly, editor]);

  if (!editor) {
    return <div className="animate-pulse bg-gray-200 h-64 rounded-lg"></div>;
  }

  // focuses the editor if the user clicks anywhere in the container
  const handleContainerClick = () => {
    if (editor && !editor.isFocused) {
      editor.commands.focus("end");
    }
  };

  return (
    <div
      className={`border border-gray-300 rich-text-editor relative ${className}`}
    >
      {/* Toolbar */}
      {!readOnly && (
        <RichTextToolbar
          editor={editor}
          editorRef={editorRef}
          isUploading={isUploading}
          fileInputRef={fileInputRef}
          enabledToolbarBlocks={enabledToolbarBlocks}
        />
      )}

      {/* Editor */}
      <div
        className="px-4 py-2 bg-white relative"
        onClick={handleContainerClick}
        ref={editorRef}
      >
        <EditorContent
          editor={editor}
          style={{ minHeight: `${rows || 3}lh` }}
        />
        {/* {!readOnly && editor.isEmpty && (
          <p className="text-gray-500 pointer-events-none absolute top-4 left-4">
            {placeholder}
          </p>
        )} */}
      </div>

      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Loading overlay */}
      {isUploading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-blue-600">Uploading image...</div>
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
